import { useEffect, useState } from "react";
import { Clock, LogIn, Loader2 } from "lucide-react";
import { getEngagementTracker } from "@/lib/api";

export function EngagementTab({ teacherId, grade }) {
  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEngagementTracker(teacherId, grade, range)
      .then(setData)
      .finally(() => setLoading(false));
  }, [teacherId, grade, range]);

  return (
    <div data-testid="engagement-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-800">Engagement</h3>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="engagement-range-toggle">
          {["today", "week"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`engagement-range-${r}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                range === r ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {r === "today" ? "Today" : "Last 7 days"}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-400" data-testid="engagement-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && data && (
        <>
          <p className="text-sm text-slate-500 mb-4" data-testid="engagement-summary">
            {data.logged_in_count} of {data.total} students logged in {range === "today" ? "today" : "in the last 7 days"}
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm" data-testid="engagement-table">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-semibold">Student</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Logged in</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Time spent</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.learner_id} className="border-t border-slate-100" data-testid={`engagement-row-${s.learner_id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{s.name}</td>
                    <td className="px-4 py-3">
                      {s.logged_in ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold">
                          <LogIn className="w-3.5 h-3.5" /> Yes
                        </span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {s.time_spent_minutes > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-700">
                          <Clock className="w-3.5 h-3.5 text-slate-400" /> {s.time_spent_minutes} min
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.students.length === 0 && (
            <div className="text-center text-slate-400 py-10" data-testid="engagement-empty">No students in this grade yet.</div>
          )}
        </>
      )}
    </div>
  );
}
